import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  ListFilter,
  MoreVertical,
  Plus,
  Search,
  Settings2,
  X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { isComboboxDismissal } from '@/components/ui/multi-id-combobox';
import { useAors } from 'hooks/useAors';
import { useAorText } from 'utils/aorTerminology';
import { AutomationRuleEditorDialog } from './AutomationRuleEditorDialog';
import { CreateOrgEventDialog } from './CreateOrgEventDialog';
import { EventAutomationSettingsPage } from './EventAutomationSettingsPage';
import { EventsFilterBar } from './EventsFilterBar';
import {
  EMPTY_EVENTS_LIST_FILTERS,
  buildEventsListFilterChips,
  filterOrgEvents,
  hasEventsListNarrowingFilters,
  type EventsArchiveFilter,
} from './eventsListFilters';
import { OrgEventListItem } from './OrgEventListItem';
import { useEnsureCfaTropicalAutomationRule } from './useEnsureCfaTropicalAutomationRule';
import { useEnsureUscgFocSeTropicalAutomationRule } from './useEnsureUscgFocSeTropicalAutomationRule';
import { useEnsureDhsTropicalAutomationRule } from './useEnsureDhsTropicalAutomationRule';
import { useEnsureCnicTropicalAutomationRule } from './useEnsureCnicTropicalAutomationRule';
import { useEnsureCnicOrgEvent } from '../LayersTab/cfa-demo/useEnsureCnicOrgEvent';
import { useEnsureOntarioWinterAutomationRule } from './useEnsureOntarioWinterAutomationRule';
import { useEnsureEquinorAutomationRule } from './useEnsureEquinorAutomationRule';
import { useEnsureJapanAutomationRule } from './useEnsureJapanAutomationRule';
import { useOrgEvents, type OrgEvent } from 'hooks/orgEvents/orgEventHooks';
import {
  filterFlagGatedDemoEvents,
  useEnabledDemoEventKeys,
} from './demoEventFlags';
import { type OrgEventAutomationRule } from 'hooks/orgEvents/orgEventAutomationHooks';
import { registerEventsTourApi } from '../../eventsTour/eventsTourBridge';
import { EVENT_TOUR_PARAM } from '../../eventsTour/eventsTourSteps';
import { useCreateIntent } from '../../useCreateIntent';

export { severityBadgeClass } from './OrgEventListItem';

export default function EventsView() {
  const enabledDemoKeys = useEnabledDemoEventKeys();
  const { data: allEvents = [], isLoading } = useOrgEvents({
    includeArchived: true,
  });
  const events = useMemo(
    () => filterFlagGatedDemoEvents(allEvents, enabledDemoKeys),
    [allEvents, enabledDemoKeys]
  );
  const { data: aors = [] } = useAors();
  const aorText = useAorText();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEventId = searchParams.get('eventId');
  const [createOpen, setCreateOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<OrgEvent | null>(null);
  const [search, setSearch] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [selectedAorIds, setSelectedAorIds] = useState<string[]>([]);
  const [archive, setArchive] = useState<EventsArchiveFilter>('active');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [triggerRule, setTriggerRule] = useState<OrgEventAutomationRule | null>(
    null
  );
  const settingsOpen = searchParams.get('eventSettings') === '1';
  const fromNotificationSettings =
    searchParams.get('from') === 'notificationSettings';

  useEnsureCfaTropicalAutomationRule();
  useEnsureUscgFocSeTropicalAutomationRule();
  useEnsureDhsTropicalAutomationRule();
  useEnsureCnicTropicalAutomationRule();
  useEnsureCnicOrgEvent();
  useEnsureOntarioWinterAutomationRule();
  useEnsureEquinorAutomationRule();
  useEnsureJapanAutomationRule();
  useCreateIntent(
    'event',
    useCallback(() => setCreateOpen(true), [])
  );

  const handleSettingsOpenChange = useCallback(
    (open: boolean) => {
      setSearchParams(
        (params) => {
          if (open) {
            params.set('eventSettings', '1');
            params.delete('from');
          } else {
            params.delete('eventSettings');
            params.delete('from');
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    return registerEventsTourApi({
      openCreateDialog: () => setCreateOpen(true),
      closeCreateDialog: () => setCreateOpen(false),
      openSettings: () => handleSettingsOpenChange(true),
      closeSettings: () => handleSettingsOpenChange(false),
    });
  }, [handleSettingsOpenChange]);

  const wantsTour = searchParams.get(EVENT_TOUR_PARAM) === '1';
  const tourStartedRef = useRef(false);

  useEffect(() => {
    if (!wantsTour || settingsOpen || isLoading || tourStartedRef.current) {
      return;
    }
    const timer = window.setTimeout(() => {
      if (tourStartedRef.current) return;
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(EVENT_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('../../eventsTour/eventsTour')
        .then(({ startEventsTour }) => {
          startEventsTour();
        })
        .catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [wantsTour, settingsOpen, isLoading, setSearchParams]);

  const handleSettingsBack = () => {
    setSearchParams(
      (params) => {
        params.delete('eventSettings');
        if (fromNotificationSettings) {
          params.delete('from');
          params.set('openTab', 'Notifications');
          params.set('notificationSettings', '1');
        } else {
          params.delete('from');
        }
        return params;
      },
      { replace: true }
    );
  };

  const aorNameById = useMemo(
    () => new Map(aors.map((aor) => [aor.id, aor.name])),
    [aors]
  );
  const aorNames = useMemo(
    () =>
      selectedAorIds
        .map((id) => aorNameById.get(id))
        .filter((name): name is string => Boolean(name)),
    [aorNameById, selectedAorIds]
  );
  const narrowingFilters = useMemo(
    () => ({ search, createdFrom, createdTo, aorNames }),
    [aorNames, createdFrom, createdTo, search]
  );
  const filtersActive = hasEventsListNarrowingFilters(narrowingFilters);
  const popoverFiltersActive =
    createdFrom.length > 0 || createdTo.length > 0 || aorNames.length > 0;
  const appliedFilterChips = useMemo(
    () =>
      buildEventsListFilterChips({
        search,
        createdFrom,
        createdTo,
        aors: selectedAorIds.flatMap((id) => {
          const name = aorNameById.get(id);
          return name ? [{ id, name }] : [];
        }),
        archive,
      }),
    [aorNameById, archive, createdFrom, createdTo, search, selectedAorIds]
  );

  const dismissFilterChip = useCallback((chipId: string) => {
    if (chipId === 'search') {
      setSearch('');
      return;
    }
    if (chipId === 'createdFrom') {
      setCreatedFrom('');
      return;
    }
    if (chipId === 'createdTo') {
      setCreatedTo('');
      return;
    }
    if (chipId === 'archive') {
      setArchive('active');
      return;
    }
    if (chipId.startsWith('aor:')) {
      const aorId = chipId.slice('aor:'.length);
      setSelectedAorIds((ids) => ids.filter((id) => id !== aorId));
    }
  }, []);

  const clearFilters = useCallback(() => {
    setSearch(EMPTY_EVENTS_LIST_FILTERS.search);
    setCreatedFrom(EMPTY_EVENTS_LIST_FILTERS.createdFrom);
    setCreatedTo(EMPTY_EVENTS_LIST_FILTERS.createdTo);
    setSelectedAorIds([]);
  }, []);

  const openRelatedEvent = useCallback(
    (related: OrgEvent) => {
      clearFilters();
      setArchive(related.archived ? 'historical' : 'active');
      setSearchParams(
        (params) => {
          params.set('eventId', related.id);
          return params;
        },
        { replace: true }
      );
    },
    [clearFilters, setSearchParams]
  );

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const aTime = a.last_update ? new Date(a.last_update).getTime() : 0;
      const bTime = b.last_update ? new Date(b.last_update).getTime() : 0;
      return bTime - aTime;
    });
  }, [events]);

  const visibleEvents = useMemo(
    () =>
      filterOrgEvents(sortedEvents, {
        search,
        createdFrom,
        createdTo,
        aorNames,
        archive,
      }),
    [aorNames, archive, createdFrom, createdTo, search, sortedEvents]
  );

  if (settingsOpen) {
    return (
      <EventAutomationSettingsPage
        onBack={handleSettingsBack}
        parentLabel={
          fromNotificationSettings ? 'Notification Settings' : 'Events'
        }
      />
    );
  }

  return (
    <div className="dark tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:gap-3 tw:bg-background tw:text-foreground">
      <div className="tw:flex tw:flex-col tw:gap-2" data-tour="events-intro">
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <h2 className="tw:m-0 tw:shrink-0 tw:text-sm tw:font-semibold">
            Events
          </h2>
          <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1.5">
            <Popover
              open={filtersOpen}
              onOpenChange={(open, eventDetails) => {
                const fromCombobox =
                  !open &&
                  (eventDetails.reason === 'outside-press' ||
                    eventDetails.reason === 'focus-out') &&
                  isComboboxDismissal(eventDetails.event);
                if (fromCombobox) {
                  eventDetails.cancel();
                  return;
                }
                setFiltersOpen(open);
              }}
            >
              <PopoverTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    aria-label="Filters"
                    aria-expanded={filtersOpen}
                    className="tw:relative"
                  >
                    <ListFilter className="tw:h-4 tw:w-4" />
                    {popoverFiltersActive ? (
                      <span
                        className="tw:absolute tw:right-1 tw:top-1 tw:size-1.5 tw:rounded-full tw:bg-primary"
                        aria-hidden
                      />
                    ) : null}
                  </Button>
                }
              />
              <PopoverContent align="end" className="dark tw:w-80 tw:p-3">
                <EventsFilterBar
                  createdFrom={createdFrom}
                  onCreatedFromChange={setCreatedFrom}
                  createdTo={createdTo}
                  onCreatedToChange={setCreatedTo}
                  selectedAorIds={selectedAorIds}
                  onAorIdsChange={setSelectedAorIds}
                />
              </PopoverContent>
            </Popover>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => handleSettingsOpenChange(true)}
              className="tw:gap-1"
              data-tour="events-settings"
            >
              <Settings2 className="tw:h-4 tw:w-4" />
              Automation Rules
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => setCreateOpen(true)}
              className="tw:gap-1"
              data-tour="events-create"
            >
              <Plus className="tw:h-4 tw:w-4" />
              Create Event
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    aria-label="Events options"
                  >
                    <MoreVertical className="tw:h-4 tw:w-4" />
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="dark tw:w-52">
                {(
                  [
                    { value: 'active', label: 'Active' },
                    { value: 'historical', label: 'Archived' },
                  ] as const
                ).map((opt) => (
                  <DropdownMenuItem
                    key={opt.value}
                    className={cn(archive === opt.value && 'tw:bg-accent')}
                    onClick={() => setArchive(opt.value)}
                  >
                    {opt.label}
                    {archive === opt.value ? ' ✓' : ''}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="tw:relative tw:w-full tw:max-w-sm">
          <Search className="tw:pointer-events-none tw:absolute tw:left-2 tw:top-1/2 tw:h-3.5 tw:w-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search events…"
            className="tw:h-8 tw:pl-8 tw:text-xs"
            aria-label="Search events"
          />
        </div>
      </div>

      {appliedFilterChips.length > 0 ? (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
          {appliedFilterChips.map((chip) => (
            <Badge
              key={chip.id}
              variant="outline"
              className="tw:h-6 tw:gap-1 tw:pr-1"
            >
              <span className="tw:max-w-[12rem] tw:truncate">{chip.label}</span>
              <button
                type="button"
                className="tw:inline-flex tw:rounded-sm tw:p-0.5 tw:text-muted-foreground tw:hover:bg-muted tw:hover:text-foreground"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => dismissFilterChip(chip.id)}
              >
                <X className="tw:size-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}

      <div
        className="tw:min-h-0 tw:min-w-0 tw:flex-1 tw:space-y-2 tw:overflow-x-hidden tw:overflow-y-auto"
        data-tour="events-list"
      >
        {isLoading ? (
          <>
            <Skeleton className="tw:h-16 tw:w-full" />
            <Skeleton className="tw:h-16 tw:w-full" />
            <Skeleton className="tw:h-16 tw:w-full" />
          </>
        ) : events.length === 0 ? (
          <Empty className="tw:border tw:border-dashed tw:border-border tw:p-8">
            <EmptyHeader>
              <EmptyTitle className="tw:text-sm">No events yet</EmptyTitle>
              <EmptyDescription>
                Create an event manually, or set up automation under Automation
                Rules.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : visibleEvents.length === 0 ? (
          <Empty className="tw:border tw:border-dashed tw:border-border tw:p-8">
            <EmptyHeader>
              <EmptyTitle className="tw:text-sm">
                {filtersActive
                  ? 'No events match'
                  : archive === 'historical'
                    ? 'No archived events'
                    : 'No active events'}
              </EmptyTitle>
              <EmptyDescription>
                {filtersActive
                  ? aorText('Try a different search, date range, or AOR.')
                  : archive === 'historical'
                    ? 'Archived events will appear here.'
                    : 'Create an event or restore one from Archived.'}
              </EmptyDescription>
            </EmptyHeader>
            {filtersActive ? (
              <EmptyContent>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={clearFilters}
                >
                  Clear filters
                </Button>
              </EmptyContent>
            ) : null}
          </Empty>
        ) : (
          visibleEvents.map((event) => (
            <OrgEventListItem
              key={event.id}
              event={event}
              defaultOpen={selectedEventId === event.id}
              onTriggerRuleClick={setTriggerRule}
              onRelatedEventClick={openRelatedEvent}
              showArchiveActions
              showEditAction
              onEdit={setEditingEvent}
            />
          ))
        )}
      </div>

      <AutomationRuleEditorDialog
        rule={triggerRule}
        open={triggerRule != null}
        onOpenChange={(open) => {
          if (!open) setTriggerRule(null);
        }}
      />

      <CreateOrgEventDialog
        open={createOpen || editingEvent != null}
        event={editingEvent ?? undefined}
        onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false);
            setEditingEvent(null);
          }
        }}
      />
    </div>
  );
}
